import * as O from 'fp-ts/Option';
import { LayoutModularFurnitureT } from './layout/modularFurniture';
import { getFurniture, getModularFurniture, LayoutFurnitureT } from './layout/furniture';
import * as Arya from '../components/arya';
import { ConfigurableProperties, ModaSofa } from '@dora/backpack';
import { v4 as uuid } from 'uuid';

export type ProductSetId = string;

/** A layout for Moda's predefined Product Sets.
 *
 * These layouts are used to represent the initial arrangement of furniture for a specific product
 * set, so that if the user starts the app for a layout we know
 *  1. what products are in it and
 *  2. how to arrange them in the scene.
 *
 * They are currently stored as hardcoded layout fixtures. They were built by hand in dora and
 * exported to JSON via the export function in dev mode.
 *
 * We later moved to using a layouts service (see data/layouts.ts) for user-created layouts, but
 * could not migrate these particular layouts to the service because ideal layouts are not aware of
 * product sets, and making a mapping would have taken longer than we had time for.
 *
 * At the moment only user-generated layouts are stored in the service, all product set layouts are
 * hardcoded in layouts.json.
 *
 * This file and its functions are kept around as long as we are still using layouts.json in order
 * to read that file and generate future layouts for product sets to add to it. Thereafter they
 * should be deleted.
 */
export type ProductSetLayout = {
    id: string;
    /** Any Moda Couches are described here */
    modularFurniture: Array<LayoutModularFurnitureT>;
    furniture: Array<LayoutFurnitureT>;
    /** Which product set is this layout for */
    productSet: ProductSetId;
    globalConfigurablePropertySelections?: ConfigurableProperties.ConfigurablePropertySelectionT[];
};

/**
 * Generate a Layout object from the current scene, a JSON representation which can be loaded
 * on initialisation based on the provided product set.
 * @param arya Used to fetch the current state (for position / rotation) from the 3d scene
 * @param furnitureList Front-end representation of products in the scene, needed primarily for modular furniture
 */
export function generate(
    arya: O.Option<Arya.Interface>,
    furnitureList: Array<{ aryaId: string; configuration: O.Option<ModaSofa.Configuration> }>,
    productSet: string = '[ TO FILL IN ]',
    globalPropSelections: ConfigurableProperties.ConfigurablePropertySelectionT[] | null,
): ProductSetLayout {
    const furniture: LayoutFurnitureT[] = getFurniture(furnitureList, arya);

    const modularFurniture: LayoutModularFurnitureT[] = getModularFurniture(furnitureList, arya);

    return {
        id: uuid(),
        modularFurniture,
        furniture,
        productSet,
        globalConfigurablePropertySelections: globalPropSelections ?? undefined,
    };
}
